/**
 * TosConsentCard — the one checkbox + attestation + terms/privacy links every product renders in
 * its own consent-gate slot (plan 2026_09_01_tos_gate.md §8.2). No button of its own: the second
 * press is the surface's own CTA (the pack CTA button, the wall's verb, the stage's verb) — this
 * card only ever gates whether that press fires.
 *
 * `styles` is injected the way `core/packs/PackPageShell.tsx` takes `PackPageStyles` — each product
 * keeps its own `.module.scss` (narcis's and ovid's are their former `TosConfirmCard.module.scss`
 * minus the button rules; the wall/toile-stage sites carry a new one on Stage Light tokens) and
 * passes it in, so one component renders on three different token sets with zero product `if`s here.
 *
 * Focus: on mount, if the checkbox is actually laid out (`offsetParent !== null`), it takes focus.
 * This is what replaces narcis's two-ref desktop/mobile workaround — both DOM sites can mount at
 * once (one hidden by CSS at the current breakpoint), and a display:none element's `.focus()` call
 * is already a silent no-op, so only the visible instance ever actually moves focus.
 *
 * `focusToken` (2026-09-01 fix round, item 3) re-runs that same focus logic on demand: without it,
 * a press that lands while the card is ALREADY mounted and still unticked (`useConsentGate`'s
 * `focus_checkbox` outcome) has no mount to trigger the effect and so moves focus nowhere, silently
 * doing nothing on what is supposed to be a nudge back to the checkbox. `useConsentGate` bumps the
 * token on that outcome (and on the original 'ask' that opens the card); this effect keys off it
 * instead of a `[]` mount-only array, so both the card's own mount and every later nudge refocus.
 * Optional: a caller that wires `checked`/`onTick` by hand instead of spreading `cardProps` keeps
 * this component's original mount-only behaviour.
 */
import {useEffect, useRef} from 'react'
import {Link} from 'react-router'

export interface TosConsentCardCopy {
    title?: string
    sub?: string
    attestation: string
    termsLabel: string
    conjunction: string
    policyLabel: string
}

export interface TosConsentCardProps {
    styles: Record<string, string>
    checked: boolean
    onTick: (checked: boolean) => void
    autoFocus?: boolean
    focusToken?: number
    termsHref: string
    privacyHref: string
    copy: TosConsentCardCopy
}

export function TosConsentCard({styles, checked, onTick, autoFocus = true, focusToken, termsHref, privacyHref, copy}: TosConsentCardProps) {
    const checkboxRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (!autoFocus) return
        const el = checkboxRef.current
        if (el && el.offsetParent !== null) el.focus()
        // Runs on mount (whatever `focusToken` starts at) and again every time `focusToken`
        // changes — a later nudge from an already-mounted card, not only the initial open (same
        // contract as the wall's own `RatioRow`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusToken])

    return (
        <div className={styles.card}>
            {(copy.title || copy.sub) && (
                <div>
                    {copy.title && <div className={styles.title}>{copy.title}</div>}
                    {copy.sub && <div className={styles.sub}>{copy.sub}</div>}
                </div>
            )}
            <label className={styles.checkbox}>
                <input
                    ref={checkboxRef}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onTick(e.target.checked)}
                />
                <span>
                    {copy.attestation}{' '}
                    <Link to={termsHref} onClick={(e) => e.stopPropagation()}>{copy.termsLabel}</Link>
                    {' '}{copy.conjunction}{' '}
                    <Link to={privacyHref} onClick={(e) => e.stopPropagation()}>{copy.policyLabel}</Link>
                </span>
            </label>
        </div>
    )
}
